import React, { useState, useEffect, useRef } from 'react';

// MultiSelect Component
const MultiSelect = ({
  name,
  value = [],
  onChange,
  options = [],
  placeholder = "Select options...",
  loading = false,
  disabled = false,
  isInvalid = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle option selection
  const handleOptionToggle = (optionValue) => {
    const currentValue = Array.isArray(value) ? value : [];
    const newValue = currentValue.includes(optionValue)
      ? currentValue.filter(v => v !== optionValue)
      : [...currentValue, optionValue];
    
    // Create synthetic event for compatibility with existing handleChange
    const event = {
      target: {
        name: name,
        value: newValue
      }
    };
    onChange(event);
  };

  // Get selected labels for display
  const getSelectedLabels = () => {
    const currentValue = Array.isArray(value) ? value : [];
    return currentValue.map(val => {
      const option = options.find(opt => opt.value.toString() === val.toString());
      return option ? option.label : val;
    });
  };

  const selectedLabels = getSelectedLabels();
  const currentValue = Array.isArray(value) ? value : [];

  return (
    <div className="position-relative" ref={dropdownRef}>
      <div
        className={`form-control d-flex align-items-center justify-content-between cursor-pointer ${
          isInvalid ? 'is-invalid' : ''
        } ${disabled ? 'disabled' : ''}`}
        style={{ 
          minHeight: '38px', 
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? '#e9ecef' : 'white'
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex-grow-1">
          {loading ? (
            <span className="text-muted">Loading...</span>
          ) : selectedLabels.length === 0 ? (
            <span className="text-muted">{placeholder}</span>
          ) : (
            <div className="d-flex flex-wrap gap-1">
              {selectedLabels.map((label, index) => (
                <span
                  key={index}
                  className="badge bg-primary d-flex align-items-center"
                  style={{ fontSize: '0.75rem' }}
                >
                  {label}
                  {!disabled && (
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-1"
                      style={{ fontSize: '0.6rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const optionValue = options.find(opt => opt.label === label)?.value;
                        if (optionValue !== undefined) {
                          handleOptionToggle(optionValue);
                        }
                      }}
                    />
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="ms-2">
          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-muted`}></i>
        </div>
      </div>

      {isOpen && !disabled && (
        <div 
          className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg"
          style={{ zIndex: 1050, maxHeight: '300px', overflow: 'hidden' }}
        >
          {/* Search input */}
          <div className="p-2 border-bottom">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control form-control-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-muted small">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`p-2 cursor-pointer d-flex align-items-center hover-bg-light ${
                    currentValue.includes(option.value) ? 'bg-light text-primary' : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOptionToggle(option.value)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => {
                    if (!currentValue.includes(option.value)) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={currentValue.includes(option.value)}
                    onChange={() => {}} // Handled by parent click
                    className="form-check-input me-2"
                    style={{ pointerEvents: 'none' }}
                  />
                  <span>{option.label}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
